import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, Checkbox, Cluster, EmptyState, HintMark, Input, Modal, Select, Stack, guardSubmit, useToast } from "@salesapp/ui";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../lib/saveNotice";
import {
  orgSettingsApi,
  type OpenWorkItem,
  type OrgRole,
  type OrgUser,
  type RecordScope,
  type SalesManagerOption,
  type TabPermission,
} from "../lib/orgSettingsApi";
import { billingApi } from "../lib/billingApi";
import { opsApi, type Territory } from "../lib/opsApi";
import { FALLBACK_TABS } from "../lib/tabPermissions";
import { useAccess } from "../shell/AccessProvider";
import { FIELD_HINTS } from "../help/fieldHints";

function setupUrl(token: string): string {
  return `${window.location.origin}/setup-password?token=${encodeURIComponent(token)}`;
}

function toggleId(current: string[], id: string, enabled: boolean): string[] {
  if (enabled) return current.includes(id) ? current : [...current, id];
  return current.filter((item) => item !== id);
}

type ReassignDialog = {
  user: OrgUser;
  mode: "disable" | "reassign";
  items: OpenWorkItem[];
  managers: SalesManagerOption[];
  managerId: string;
};

function seatLabel(user: OrgUser): string {
  const seats = [
    user.crm_seat ? "CRM" : "",
    user.timesheet_seat ? "Timesheet" : "",
    user.expense_seat ? "Expense" : "",
  ].filter(Boolean);
  return seats.length > 0 ? seats.join(", ") : "None";
}

function permissionLabel(user: OrgUser, tabList: TabPermission[]): string {
  if (user.is_org_admin) return "All licensed tabs";
  const labels = user.capabilities.map((capability) => tabList.find((tab) => tab.capability === capability)?.label ?? capability);
  return labels.length > 0 ? labels.join(", ") : "No tab access";
}

function matchesQuery(query: string, ...parts: Array<string | null | undefined>): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return parts.some((part) => (part ?? "").toLowerCase().includes(needle));
}

function personMatches(user: OrgUser, roleList: OrgRole[], query: string): boolean {
  const assigned = roleList
    .filter((role) => user.is_org_admin || (user.role_ids ?? []).includes(role.id))
    .map((role) => role.name);
  return matchesQuery(
    query,
    user.display_name,
    user.email,
    user.department,
    user.territory_name,
    user.is_org_admin ? "organization admin" : "",
    ...assigned,
  );
}

function tierLabel(minTier?: string): string {
  if (minTier === "professional") return "Professional";
  if (minTier === "enterprise") return "Enterprise";
  if (minTier === "standard") return "Add-on";
  return "";
}

function RoleAccessTable({
  role,
  tabs,
  adminOnlyTabs,
  licensed,
  onSave,
}: {
  role: OrgRole;
  tabs: TabPermission[];
  adminOnlyTabs: TabPermission[];
  licensed: string[];
  onSave: (capabilities: string[], adminCapabilities: string[]) => void;
}) {
  const adminCaps = role.admin_capabilities ?? [];
  return (
    <div className="permission-table-wrap">
      <table className="role-matrix">
        <thead>
          <tr>
            <th scope="col">Tab</th>
            <th scope="col" className="role-matrix-check">
              <span className="role-matrix-heading">
                User
                <HintMark label="About User" text={FIELD_HINTS.people_tab_checkbox} />
              </span>
            </th>
            <th scope="col" className="role-matrix-check">
              <span className="role-matrix-heading">
                Admin
                <HintMark label="About Admin" text={FIELD_HINTS.people_tab_admin_checkbox} />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {tabs.map((tab) => {
            const onPlan = licensed.includes(tab.capability);
            const locked = tierLabel(tab.min_tier);
            const hasTab = role.capabilities.includes(tab.capability);
            const isAdmin = adminCaps.includes(tab.capability);
            return (
              <tr key={tab.capability}>
                <th scope="row" className="role-matrix-name">
                  {tab.label}
                  {!onPlan && locked ? <span className="tier-lock">{locked}</span> : null}
                </th>
                <td className="role-matrix-check">
                  <Checkbox
                    aria-label={tab.label}
                    checked={hasTab}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      const nextCaps = toggleId(role.capabilities, tab.capability, enabled);
                      const nextAdmin = enabled
                        ? adminCaps
                        : adminCaps.filter((item) => item !== tab.capability);
                      onSave(nextCaps, nextAdmin);
                    }}
                  />
                </td>
                <td className="role-matrix-check">
                  <Checkbox
                    aria-label={`Admin for ${tab.label}`}
                    disabled={!hasTab}
                    checked={isAdmin}
                    onChange={(event) => {
                      onSave(role.capabilities, toggleId(adminCaps, tab.capability, event.target.checked));
                    }}
                  />
                </td>
              </tr>
            );
          })}
          {adminOnlyTabs.map((tab) => {
            const isAdmin = adminCaps.includes(tab.capability);
            return (
              <tr key={tab.capability}>
                <th scope="row" className="role-matrix-name">{tab.label}</th>
                <td className="role-matrix-check">
                  <span className="role-matrix-empty" aria-hidden="true">—</span>
                </td>
                <td className="role-matrix-check">
                  <Checkbox
                    aria-label={`Admin for ${tab.label}`}
                    checked={isAdmin}
                    onChange={(event) => {
                      onSave(role.capabilities, toggleId(adminCaps, tab.capability, event.target.checked));
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


const RECORD_SCOPE_LABELS: Record<RecordScope, string> = {
  own: "Own and direct reports",
  territory: "Everything in their territories",
  all: "Everything in every territory",
};

function RecordScopeSelect({
  id, value, onChange, disabled = false, note,
}: {
  id: string;
  value: RecordScope;
  onChange: (scope: RecordScope) => void;
  disabled?: boolean;
  note?: string;
}) {
  return (
    <Select
      id={id}
      label="Records this role can see"
      tooltip={FIELD_HINTS.people_role_record_scope}
      aside={note ? <span className="role-scope-fixed">{note}</span> : undefined}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as RecordScope)}
    >
      {(Object.keys(RECORD_SCOPE_LABELS) as RecordScope[]).map((scope) => (
        <option key={scope} value={scope}>{RECORD_SCOPE_LABELS[scope]}</option>
      ))}
    </Select>
  );
}

export function PeoplePanel({ orgId, section }: { orgId: string; section: "users" | "roles" }) {
  const { show } = useToast();
  const { can } = useAccess();
  const canAssignTerritory = can("crm.territories");
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [licensed, setLicensed] = useState<string[]>([]);
  const [tabs, setTabs] = useState<TabPermission[]>(FALLBACK_TABS);
  const [adminOnlyTabs, setAdminOnlyTabs] = useState<TabPermission[]>([]);
  const [canCreateRoles, setCanCreateRoles] = useState(false);
  const [rolesReady, setRolesReady] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customScope, setCustomScope] = useState<RecordScope>("own");
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [crmSeatLimit, setCrmSeatLimit] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const [setupNotice, setSetupNotice] = useState(
    "Copy this once and send it to the person. It expires in 15 minutes and is not a password.",
  );
  const [setupUser, setSetupUser] = useState<OrgUser | null>(null);
  const [setupBusy, setSetupBusy] = useState<"email" | "copy" | null>(null);
  const [reassign, setReassign] = useState<ReassignDialog | null>(null);
  const [reassignBusy, setReassignBusy] = useState(false);
  const [personQuery, setPersonQuery] = useState("");
  const [roleQuery, setRoleQuery] = useState("");
  const [form, setForm] = useState({
    email: "",
    display_name: "",
    department: "",
    is_org_admin: false,
    role_ids: [] as string[],
    reports_to_user_id: "",
    territory_id: "",
  });

  async function load(quiet = false) {
    if (!quiet) setError(null);
    try {
      const [result, territoryRows] = await Promise.all([
        orgSettingsApi.listUsers(orgId),
        opsApi.listTerritories(orgId).then((row) => row.territories).catch(() => [] as Territory[]),
      ]);
      setUsers(result.users);
      setLicensed(result.licensed_capabilities ?? []);
      if (result.tabs?.length) setTabs(result.tabs);
      setAdminOnlyTabs(result.admin_only_tabs ?? []);
      if (result.roles?.length) setRoles(result.roles.filter((role) => role.code !== "marketing"));
      setCanCreateRoles(Boolean(result.can_create_custom_roles));
      setRolesReady(true);
      setTerritories(territoryRows);
      try {
        const billing = await billingApi.get(orgId);
        const license = billing.licenses.find((item) => item.app_code === "crm");
        setCrmSeatLimit(license?.seats ?? null);
      } catch {
        setCrmSeatLimit(null);
      }
      setForm((current) => {
        if (current.territory_id) return current;
        const globalRow = territoryRows.find((item) => item.code === "global") ?? territoryRows[0];
        return globalRow ? { ...current, territory_id: globalRow.id } : current;
      });
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof Error ? err.message : "Users could not be loaded.");
    }
  }

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      void load(true);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [orgId]);

  async function saveRole(
    role: OrgRole, capabilities: string[], adminCapabilities: string[], recordScope?: RecordScope,
  ) {
    setError(null);
    try {
      const updated = await orgSettingsApi.updateRole(orgId, role.id, {
        capabilities,
        admin_capabilities: adminCapabilities,
        ...(recordScope ? { record_scope: recordScope } : {}),
      });
      setRoles((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Role could not be updated."));
    }
  }

  async function onCreateRole(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const created = await orgSettingsApi.createRole(orgId, {
        name: customName,
        description: customDescription || undefined,
        record_scope: customScope,
      });
      setRoles((current) => [...current.filter((item) => item.id !== created.id), created]);
      setCustomName("");
      setCustomDescription("");
      setCustomScope("own");
      reportSaved(show, "Role created.");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Role could not be created."));
    }
  }

  async function onDeleteRole(role: OrgRole) {
    setError(null);
    try {
      await orgSettingsApi.deleteRole(orgId, role.id);
      setRoles((current) => current.filter((item) => item.id !== role.id));
      setForm((current) => ({ ...current, role_ids: current.role_ids.filter((id) => id !== role.id) }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Role could not be deleted.");
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const created = await orgSettingsApi.createUser(orgId, {
        email: form.email,
        display_name: form.display_name,
        department: form.department,
        is_org_admin: form.is_org_admin,
        is_app_admin: false,
        capabilities: [],
        role_ids: form.role_ids,
        reports_to_user_id: form.reports_to_user_id || null,
        territory_id: canAssignTerritory ? form.territory_id || null : null,
      });
      if (created.setup_token) {
        setSetupLink(setupUrl(created.setup_token));
        setSetupNotice("Copy this once and send it, or use Setup link on their row to email it. It expires in 15 minutes.");
      }
      setForm({ ...form, email: "", display_name: "", department: "", role_ids: [] });
      reportSaved(show, "Person added.");
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "User could not be created."));
    }
  }

  async function saveUser(user: OrgUser, patch: Record<string, unknown>) {
    setError(null);
    try {
      await orgSettingsApi.updateUser(orgId, user.user_id, patch);
      reportSaved(show);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "User could not be updated."));
    }
  }

  async function beginReassign(user: OrgUser, mode: "disable" | "reassign") {
    setError(null);
    try {
      const preview = await orgSettingsApi.openItems(orgId, user.user_id);
      if (preview.total === 0) {
        if (mode === "disable") {
          await saveUser(user, { membership_status: "revoked" });
        } else {
          setError(reportSaveBlocked(show, "No open work is assigned to this person."));
        }
        return;
      }
      setReassign({
        user,
        mode,
        items: preview.items,
        managers: preview.sales_managers,
        managerId: preview.sales_managers[0]?.user_id ?? "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Open work could not be checked.");
    }
  }

  async function confirmReassign() {
    if (!reassign?.managerId) return;
    setError(null);
    setReassignBusy(true);
    try {
      const patch: Record<string, unknown> = { reassign_to_user_id: reassign.managerId };
      if (reassign.mode === "disable") patch.membership_status = "revoked";
      await orgSettingsApi.updateUser(orgId, reassign.user.user_id, patch);
      setReassign(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Open work could not be reassigned.");
    } finally {
      setReassignBusy(false);
    }
  }

  async function deliverSetup(user: OrgUser, deliver: "email" | "copy") {
    setError(null);
    setSetupBusy(deliver);
    try {
      const result = await orgSettingsApi.issueSetupLink(orgId, user.user_id, deliver);
      const url = setupUrl(result.setup_token);
      setSetupLink(url);
      if (deliver === "copy") {
        await navigator.clipboard.writeText(url);
        setSetupNotice("Copied. Send this link to the person — it expires in 15 minutes and is not a password.");
      } else if (result.emailed) {
        setSetupNotice(`Sent to ${result.email}. They have 15 minutes to set a password.`);
      } else if (result.delivery === "not_configured") {
        await navigator.clipboard.writeText(url).catch(() => undefined);
        setSetupNotice("Email isn't configured here. The link is copied — send it to the person yourself.");
      } else {
        await navigator.clipboard.writeText(url).catch(() => undefined);
        setSetupNotice("The email couldn't be sent. The link is copied — send it to the person yourself.");
      }
      setSetupUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup link could not be created.");
    } finally {
      setSetupBusy(null);
    }
  }

  const seatsUsed = users.filter((user) => user.crm_seat).length;
  const planFull = crmSeatLimit != null && seatsUsed >= crmSeatLimit;
  const visibleRoles = roles.filter((role) => matchesQuery(roleQuery, role.name, role.description));
  const visiblePeople = users.filter((user) => personMatches(user, roles, personQuery));

  return (
    <Stack gap={5}>
      {error && <p role="alert" className="settings-error">{error}</p>}
      {section === "roles" && (
        <Stack gap={4} data-tour="people-roles">
          <p className="home-subtitle">
            Preconfigured roles bundle the tabs a person can open. App admin only elevates those tabs — a sales person who is also app admin does not get Support, Dashboards, or Documents. Tabs above the current plan stay visible so they light up after an upgrade.
          </p>
          {rolesReady && canCreateRoles ? (
            <Card>
              <form onSubmit={guardSubmit(onCreateRole)}>
                <Stack gap={4}>
                  <h2 className="home-section-title">Create a custom role</h2>
                  <p className="home-subtitle">Name the role, then tick the tabs it should open. Assign it to people on the Users tab.</p>
                  <Cluster gap={3} align="end">
                    <Input
                      label="Role name"
                      required
                      value={customName}
                      onChange={(event) => setCustomName(event.target.value)}
                    />
                    <Input
                      label="Description"
                      value={customDescription}
                      onChange={(event) => setCustomDescription(event.target.value)}
                    />
                    <RecordScopeSelect id="new-role-record-scope" value={customScope} onChange={setCustomScope} />
                    <Button type="submit">Create role</Button>
                  </Cluster>
                </Stack>
              </form>
            </Card>
          ) : rolesReady ? (
            <p className="home-subtitle">Custom roles are available on Professional. Basic uses the preconfigured roles below.</p>
          ) : null}
          {roles.length === 0 ? (
            <EmptyState title="Loading roles" description="Named roles are created for this organization on first visit." />
          ) : (
            <>
          <Input
            aria-label="Search roles"
            value={roleQuery}
            onChange={(event) => setRoleQuery(event.target.value)}
            placeholder="Search roles"
          />
          {visibleRoles.length === 0 ? (
            <EmptyState title="No roles match" description="Try a different role name." />
          ) : visibleRoles.map((role) => (
              <Card key={role.id}>
                <Stack gap={3}>
                  <Cluster gap={3} align="start" justify="between">
                    <div>
                      <h2 className="home-section-title">{role.name}</h2>
                      <p className="home-subtitle">{role.is_system ? role.description : (role.description || "Custom role")}</p>
                    </div>
                    <Cluster gap={3} align="center">
                      <p className="role-count">
                        {users.filter((item) => item.membership_status === "active" && (item.is_org_admin || (item.role_ids ?? []).includes(role.id))).length}
                        {" "}
                        active users assigned
                      </p>
                      {!role.is_system && (
                        <Button type="button" variant="danger" size="sm" onClick={() => onDeleteRole(role)}>
                          Delete
                        </Button>
                      )}
                    </Cluster>
                  </Cluster>
                  <p className="home-subtitle">
                    Each row is one tab. User lets the role open it. Admin lets the role change that tab&apos;s settings.
                  </p>
                  {role.is_app_admin && (
                    <p className="home-subtitle">App admin starts with Admin on every tab it grants. Clear Admin on a row to narrow it.</p>
                  )}
                  {role.is_app_admin ? (
                    <p className="home-subtitle">Records: an app admin sees everything in every territory.</p>
                  ) : (
                    <RecordScopeSelect
                      id={`role-record-scope-${role.id}`}
                      value={role.record_scope}
                      disabled={role.is_system}
                      note={role.is_system ? "Application role. Which records it can see cannot be changed. Create a custom role for a different one." : undefined}
                      onChange={(scope) => void saveRole(role, role.capabilities, role.admin_capabilities, scope)}
                    />
                  )}
                  <RoleAccessTable
                    role={role}
                    tabs={tabs}
                    adminOnlyTabs={adminOnlyTabs}
                    licensed={licensed}
                    onSave={(capabilities, adminCapabilities) => {
                      void saveRole(role, capabilities, adminCapabilities);
                    }}
                  />
                </Stack>
              </Card>
            ))}
            </>
          )}
        </Stack>
      )}
      {section === "users" && (
        <Stack gap={5}>
          {setupLink && (
            <Card>
              <Stack gap={2}>
                <strong>Password setup link</strong>
                <p className="home-subtitle">{setupNotice}</p>
                <code className="settings-setup-link">{setupLink}</code>
              </Stack>
            </Card>
          )}
          <Card data-tour="add-person">
            <form onSubmit={guardSubmit(onCreate)}>
              <Stack gap={4}>
                <h2 className="home-section-title">Add a person</h2>
                {planFull && (
                  <p className="crm-warning" role="alert">
                    This plan has {crmSeatLimit} CRM seats and all of them are in use. Upgrade the plan before adding another person.
                  </p>
                )}
                <Cluster gap={3}>
                  <Input label="Email" type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                  <Input label="Name" required value={form.display_name} onChange={(event) => setForm({ ...form, display_name: event.target.value })} />
                  <Input label="Department" value={form.department} onChange={(event) => setForm({ ...form, department: event.target.value })} />
                </Cluster>
                <Cluster gap={3} align="end">
                  <Select
                    label="Reports to"
                    tooltip={FIELD_HINTS.people_reports_to}
                    value={form.reports_to_user_id}
                    onChange={(event) => setForm({ ...form, reports_to_user_id: event.target.value })}
                  >
                    <option value="">None</option>
                    {users.filter((item) => item.membership_status === "active").map((item) => (
                      <option key={item.user_id} value={item.user_id}>{item.display_name} ({item.email})</option>
                    ))}
                  </Select>
                  {canAssignTerritory && territories.length > 0 && (
                    <Select
                      label="Territory"
                      tooltip={FIELD_HINTS.people_territory}
                      value={form.territory_id}
                      onChange={(event) => setForm({ ...form, territory_id: event.target.value })}
                    >
                      {territories.map((territory) => (
                        <option key={territory.id} value={territory.id}>{territory.name}</option>
                      ))}
                    </Select>
                  )}
                  <Checkbox
                    label="Organization admin"
                    checked={form.is_org_admin}
                    tooltip={FIELD_HINTS.people_org_admin}
                    onChange={(event) => setForm({ ...form, is_org_admin: event.target.checked })}
                  />
                </Cluster>
                <div>
                  <p className="home-subtitle">Roles — assign one or more. App admin only configures the tabs from the other roles.</p>
                  <div className="permission-grid">
                    {roles.map((role) => (
                      <Checkbox
                        key={role.id}
                        label={role.name}
                        tooltip={FIELD_HINTS.people_role_checkbox}
                        checked={form.role_ids.includes(role.id)}
                        onChange={(event) => setForm({ ...form, role_ids: toggleId(form.role_ids, role.id, event.target.checked) })}
                      />
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={planFull}>Add user</Button>
              </Stack>
            </form>
          </Card>
          <Card>
            <Stack gap={4}>
              <Cluster gap={3} align="center" justify="between">
                <h2 className="home-section-title">People</h2>
                <p className="people-count">
                  {users.filter((item) => item.membership_status === "active").length} active users
                  {crmSeatLimit != null ? ` · ${seatsUsed} of ${crmSeatLimit} CRM seats` : ""}
                </p>
              </Cluster>
            {users.length === 0 ? (
              <EmptyState title="No people yet" description="Add the first person and assign one or more roles." />
            ) : (
              <>
              <Input
                aria-label="Search people"
                value={personQuery}
                onChange={(event) => setPersonQuery(event.target.value)}
                placeholder="Search name, email, or department"
              />
              {visiblePeople.length === 0 ? (
                <EmptyState title="No people match" description="Try a different name, email, or department." />
              ) : (
              <div className="permission-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Person</th>
                      <th>Reports to</th>
                      <th>Territory</th>
                      <th>Org admin</th>
                      <th>Roles</th>
                      <th>Seats</th>
                      <th>Permissions</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePeople.map((user) => (
                      <tr key={user.user_id}>
                        <td>
                          <strong>{user.display_name}</strong>
                          <div className="home-subtitle">{user.email}{user.department ? ` · ${user.department}` : ""}</div>
                        </td>
                        <td>
                          <Select
                            tooltip={FIELD_HINTS.people_reports_to}
                            value={user.reports_to_user_id ?? ""}
                            onChange={(event) => {
                              const value = event.target.value;
                              void saveUser(user, value ? { reports_to_user_id: value } : { clear_reports_to: true });
                            }}
                          >
                            <option value="">None</option>
                            {users.filter((item) => item.user_id !== user.user_id && item.membership_status === "active").map((item) => (
                              <option key={item.user_id} value={item.user_id}>{item.display_name}</option>
                            ))}
                          </Select>
                        </td>
                        <td>
                          {canAssignTerritory && territories.length > 0 ? (
                            <Select
                              tooltip={FIELD_HINTS.people_territory}
                              value={user.territory_id ?? territories.find((item) => item.code === "global")?.id ?? ""}
                              onChange={(event) => void saveUser(user, { territory_id: event.target.value })}
                            >
                              {territories.map((territory) => (
                                <option key={territory.id} value={territory.id}>{territory.name}</option>
                              ))}
                            </Select>
                          ) : (user.territory_name ?? "Global")}
                        </td>
                        <td>
                          <Checkbox
                            aria-label={`Organization admin for ${user.display_name}`}
                            tooltip={FIELD_HINTS.people_org_admin}
                            checked={user.is_org_admin}
                            onChange={(event) => void saveUser(user, { is_org_admin: event.target.checked })}
                          />
                        </td>
                        <td>
                          <div className="role-chip-grid">
                            {roles.map((role) => (
                              <Checkbox
                                key={role.id}
                                label={role.name}
                                tooltip={FIELD_HINTS.people_role_checkbox}
                                disabled={user.is_org_admin || user.membership_status === "revoked"}
                                checked={user.is_org_admin || (user.role_ids ?? []).includes(role.id)}
                                onChange={(event) => {
                                  const next = toggleId(user.role_ids ?? [], role.id, event.target.checked);
                                  void saveUser(user, { role_ids: next });
                                }}
                              />
                            ))}
                          </div>
                        </td>
                        <td>{seatLabel(user)}</td>
                        <td>{permissionLabel(user, tabs)}</td>
                        <td>{user.membership_status === "revoked" ? "Disabled" : user.status}</td>
                        <td>
                          <Cluster gap={2}>
                            <Button type="button" variant="secondary" size="sm" onClick={() => setSetupUser(user)}>
                              Setup link
                            </Button>
                            {user.membership_status === "revoked" && (
                              <Button type="button" variant="secondary" size="sm" onClick={() => void beginReassign(user, "reassign")}>
                                Reassign open work
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                if (user.membership_status === "revoked") {
                                  void saveUser(user, { membership_status: "active" });
                                  return;
                                }
                                void beginReassign(user, "disable");
                              }}
                            >
                              {user.membership_status === "revoked" ? "Restore" : "Disable"}
                            </Button>
                          </Cluster>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              </>
            )}
            </Stack>
          </Card>
          <Modal
            isOpen={setupUser !== null}
            onClose={() => { if (!setupBusy) setSetupUser(null); }}
            title="Password setup link"
            footer={(
              <>
                <Button type="button" variant="secondary" disabled={Boolean(setupBusy)} onClick={() => setSetupUser(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={Boolean(setupBusy)}
                  onClick={() => (setupUser ? deliverSetup(setupUser, "copy") : undefined)}
                >
                  {setupBusy === "copy" ? "Copying…" : "Copy link"}
                </Button>
                <Button
                  type="button"
                  disabled={Boolean(setupBusy)}
                  onClick={() => (setupUser ? deliverSetup(setupUser, "email") : undefined)}
                >
                  {setupBusy === "email" ? "Sending…" : `Email ${setupUser?.email ?? ""}`}
                </Button>
              </>
            )}
          >
            <p className="home-subtitle">
              Send a one-time password setup or reset link to {setupUser?.email}, or copy it to share yourself.
              They do not need a verification code. The link expires in 15 minutes and is for password accounts, not SSO.
            </p>
          </Modal>
          <Modal
            isOpen={reassign !== null}
            onClose={() => { if (!reassignBusy) setReassign(null); }}
            title="Reassign open work"
            footer={(
              <>
                <Button type="button" variant="secondary" disabled={reassignBusy} onClick={() => setReassign(null)}>
                  Cancel
                </Button>
                <Button type="button" disabled={reassignBusy || !reassign?.managerId} onClick={() => void confirmReassign()}>
                  {reassignBusy ? "Saving…" : reassign?.mode === "disable" ? "Disable and reassign" : "Reassign"}
                </Button>
              </>
            )}
          >
            <Stack gap={3}>
              <p className="home-subtitle">
                {reassign?.user.display_name} still owns open sales work. Choose a sales manager to take it so those
                deals keep moving. Closed quotes, orders, invoices, and won or lost deals stay with{" "}
                {reassign?.user.display_name} as history.
              </p>
              <Stack gap={1}>
                {reassign?.items.map((item) => (
                  <p key={item.key}>{item.label}: {item.count}</p>
                ))}
              </Stack>
              {reassign && reassign.managers.length === 0 ? (
                <p className="home-subtitle">
                  There is no other active sales manager. Give someone the Sales manager role, then come back to move this work.
                </p>
              ) : (
                <Select
                  label="Sales manager"
                  value={reassign?.managerId ?? ""}
                  onChange={(event) => {
                    const managerId = event.target.value;
                    setReassign((current) => (current ? { ...current, managerId } : current));
                  }}
                >
                  {(reassign?.managers ?? []).map((manager) => (
                    <option key={manager.user_id} value={manager.user_id}>
                      {manager.display_name} ({manager.email})
                    </option>
                  ))}
                </Select>
              )}
            </Stack>
          </Modal>
        </Stack>
      )}
    </Stack>
  );
}
